/**
 * Device fallback / thank-you stage presentation tokens.
 *
 * Kept out of theme.ts so the shells own their vocabulary, and because theme.ts
 * is already far past a comfortable size.
 *
 * Same model as the thank-you page: presentation + copy are flat theme keys.
 * Which blocked devices see this screen is a separate flat toggle bag
 * (`deviceFallbackEnabledFor`), parallel to `supportedDevices` — not a nested
 * presentation object.
 */

/**
 * Which blocked device classes see the fallback screen.
 *
 * Unset means shown, so a campaign that configures nothing gets the designed screen.
 * Switching a class off returns it to the pre-fallback behaviour of going straight to the
 * CTA, which is the escape hatch for campaigns that would rather not explain the block.
 */
export interface DeviceFallbackToggles {
  ios?: boolean;
  android?: boolean;
  desktop?: boolean;
}

/**
 * Optional presentation overrides (flat on the theme, or passed into the merger).
 * Not a Redis nest — admin and player both treat these as top-level theme fields.
 */
export interface DeviceFallbackConfig {
  /** Dark purple backdrop showing wherever the campaign media does not reach. */
  stageColor?: string;
  /** Body copy, labels and quiet actions. A pale lilac rather than white, which is what
   *  ties the type to the purple stage instead of letting it read as a system dialog. */
  copyColor?: string;
  /** Hairline seam where the desktop scan panel meets the story side. */
  accentColor?: string;
  /** QR outline gradient, yellow into vivid purple. */
  qrRimStartColor?: string;
  qrRimEndColor?: string;
  /** Blur applied to the campaign media behind the copy. */
  backgroundBlurPx?: number;
  /** Blur of the media seen *through* the desktop scan panel. Larger than the background
   *  blur on purpose: this one is frosted glass, not a soft backdrop. */
  panelBlurPx?: number;

  /**
   * Logo heights, as a fraction of the stage width.
   *
   * Split by variant because the two screens frame the pair completely differently — a
   * corner lockup on the desktop stage versus a centred hero on the phone — so a single
   * number cannot flatter both. Fractions rather than pixels so a logo keeps its
   * proportion of the composition on any screen.
   */
  desktopStudioLogoScale?: number;
  desktopGameLogoScale?: number;
  mobileStudioLogoScale?: number;
  mobileGameLogoScale?: number;

  // Desktop variant copy (mobile-locked campaign opened on a computer)
  desktopHeadline?: string;
  desktopSubcopy?: string;
  desktopQrCaption?: string;

  // Mobile variant copy (desktop-locked campaign opened on a phone)
  mobileHeadline?: string;
  mobileSubcopy?: string;
  mobileEmailLabel?: string;
  mobileEmailPlaceholder?: string;
  mobileSubmitLabel?: string;
  mobileCopyLinkLabel?: string;
  /** Shown once the link is away. Names the target device, so it cannot say "computer"
   *  when the visitor is being sent to the other phone platform. */
  mobileSentSubcopy?: string;

  // Shown when the campaign is playable on no device the visitor can reach
  unavailableHeadline?: string;
  unavailableSubcopy?: string;
}

export type DetectedDeviceType = 'ios' | 'android' | 'desktop';

/**
 * The device class this visitor should be sent to, which is not simply "the one they
 * aren't on": an iOS-only campaign blocks Android *and* desktop, so an Android visitor
 * has to be pointed at an iPhone rather than at a computer they also cannot use.
 *
 * 'none' means nothing is playable anywhere, which only happens if a campaign disables
 * every platform.
 */
export type HandoffTarget = 'mobile' | 'desktop' | 'ios' | 'android' | 'none';

interface SupportedDevices {
  ios?: boolean;
  android?: boolean;
  desktop?: boolean;
}

export const getHandoffTarget = (
  devices: SupportedDevices | undefined,
  detectedDeviceType: DetectedDeviceType,
): HandoffTarget => {
  const iosAllowed = devices?.ios !== false;
  const androidAllowed = devices?.android !== false;
  const desktopAllowed = devices?.desktop !== false;

  if (detectedDeviceType === 'desktop') {
    if (iosAllowed && androidAllowed) return 'mobile';
    if (iosAllowed) return 'ios';
    if (androidAllowed) return 'android';
    return 'none';
  }

  // A blocked phone: prefer a computer, but fall back to the other phone platform so we
  // never advertise a device that is itself blocked.
  if (desktopAllowed) return 'desktop';
  if (detectedDeviceType === 'ios' && androidAllowed) return 'android';
  if (detectedDeviceType === 'android' && iosAllowed) return 'ios';
  return 'none';
};

/** Presentation with every field filled in from defaults + overrides. */
export type ResolvedDeviceFallbackConfig = Required<DeviceFallbackConfig>;

/**
 * Whether a blocked device should see the fallback screen rather than the CTA.
 *
 * Prefer the flat `deviceFallbackEnabledFor` theme field; `legacyEnabledFor` is only for
 * campaigns that still have the old nested `deviceFallback.enabledFor`.
 */
export const isDeviceFallbackEnabled = (
  enabledFor: DeviceFallbackToggles | undefined,
  detectedDeviceType: DetectedDeviceType,
): boolean => enabledFor?.[detectedDeviceType] !== false;

/**
 * Resolve the toggle bag from a theme. Flat key wins; legacy nest is a read-only fallback
 * until the campaign is re-saved from admin (which clears the nest).
 */
export const resolveDeviceFallbackEnabledFor = (theme: {
  deviceFallbackEnabledFor?: DeviceFallbackToggles;
  deviceFallback?: { enabledFor?: DeviceFallbackToggles };
}): DeviceFallbackToggles | undefined =>
  theme.deviceFallbackEnabledFor ?? theme.deviceFallback?.enabledFor;

export const DEVICE_FALLBACK_DEFAULTS: ResolvedDeviceFallbackConfig = {
  stageColor: '#1A0B2E',
  copyColor: '#E9D5FF',
  accentColor: '#C084FC',
  qrRimStartColor: '#FBBF24',
  qrRimEndColor: '#9333EA',
  backgroundBlurPx: 4,
  panelBlurPx: 14,

  // The desktop pair, as canvas pixels over that artboard's 873: the game logo leads at 72
  // and the studio mark credits it at exactly half that, 36. The studio used to stand taller
  // than the game it was crediting, which read the wrong way round on a campaign screen.
  // Heights only — `ThemeLogo` pins `width: auto` and `object-fit: contain` last, so each
  // logo keeps its own aspect ratio whatever height it is given.
  //
  // Mobile: studio at 68.4 / 390 (−10% from the canvas 76) and game at 106.48 / 390
  // (+10% twice from the canvas 88), so the hero lockup leans further toward the game.
  desktopStudioLogoScale: 0.0412,
  desktopGameLogoScale: 0.0824,
  mobileStudioLogoScale: 0.1754,
  mobileGameLogoScale: 0.2730,

  desktopHeadline: 'Jump In On Mobile',
  desktopSubcopy: 'Scan to continue on your phone. Instant play, no install.',
  desktopQrCaption: 'Scan With Your Phone',

  mobileHeadline: 'Jump in on PC',
  mobileSubcopy: 'Email yourself a link. Instant play, no install.',
  mobileEmailLabel: 'Email',
  mobileEmailPlaceholder: 'name@email.com',
  mobileSubmitLabel: 'Email Me The Link',
  mobileCopyLinkLabel: 'Copy Link',
  mobileSentSubcopy: 'Your game is waiting. Open this link on your computer to jump in.',

  unavailableHeadline: 'Not available on this device',
  unavailableSubcopy: 'This campaign is not playable here yet. Check back soon.',
};

const MOBILE_PLATFORM_LABEL: Record<'ios' | 'android', string> = {
  ios: 'iPhone',
  android: 'Android',
};

/**
 * Design defaults for one handoff target.
 *
 * The stock mobile copy names a computer, which is only true when desktop is the target.
 * When the visitor has to move to the other phone platform instead, every line that names
 * a device is rewritten — headline *and* the confirmation that follows the send, since a
 * screen that says "Jump in on Android" and then "open it on your computer" contradicts
 * itself. Applied as *defaults*, so a campaign's own overrides still win in the merge.
 */
const defaultsForTarget = (target: HandoffTarget): ResolvedDeviceFallbackConfig => {
  if (target !== 'ios' && target !== 'android') return DEVICE_FALLBACK_DEFAULTS;

  const label = MOBILE_PLATFORM_LABEL[target];
  return {
    ...DEVICE_FALLBACK_DEFAULTS,
    mobileHeadline: `Jump in on ${label}`,
    mobileSentSubcopy: `Your game is waiting. Open this link on your ${label} to jump in.`,
  };
};

/**
 * Merge a campaign's overrides over the design defaults.
 * Blank strings are treated as unset so clearing a field in the dashboard restores the
 * approved copy rather than rendering an empty heading.
 */
export const getDeviceFallbackConfig = (
  overrides: DeviceFallbackConfig | undefined,
  handoffTarget: HandoffTarget = 'desktop',
): ResolvedDeviceFallbackConfig => {
  const defaults = defaultsForTarget(handoffTarget);
  if (!overrides) return defaults;

  const merged = { ...defaults };

  (Object.keys(DEVICE_FALLBACK_DEFAULTS) as Array<keyof ResolvedDeviceFallbackConfig>).forEach((key) => {
    const value = overrides[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      (merged[key] as number) = value;
    } else if (typeof value === 'string' && value.trim().length > 0) {
      (merged[key] as string) = value;
    }
  });

  return merged;
};

const FLAT_PRESENTATION_KEYS = [
  'stageColor',
  'copyColor',
  'accentColor',
  'qrRimStartColor',
  'qrRimEndColor',
  'backgroundBlurPx',
  'panelBlurPx',
  'desktopStudioLogoScale',
  'desktopGameLogoScale',
  'mobileStudioLogoScale',
  'mobileGameLogoScale',
  'desktopHeadline',
  'desktopSubcopy',
  'desktopQrCaption',
  'mobileHeadline',
  'mobileSubcopy',
  'mobileEmailLabel',
  'mobileEmailPlaceholder',
  'mobileSubmitLabel',
  'mobileCopyLinkLabel',
  'mobileSentSubcopy',
  'unavailableHeadline',
  'unavailableSubcopy',
] as const satisfies ReadonlyArray<keyof ResolvedDeviceFallbackConfig>;

/**
 * Resolve thank-you / device-fallback presentation from flat theme keys only.
 *
 * Same pattern as the thank-you page: no nested presentation bag. Clearing a
 * flat field restores the design default.
 */
export const resolveDeviceFallbackConfig = (
  theme: Partial<ResolvedDeviceFallbackConfig>,
  handoffTarget: HandoffTarget = 'desktop',
): ResolvedDeviceFallbackConfig => {
  const flat: Partial<DeviceFallbackConfig> = {};

  for (const key of FLAT_PRESENTATION_KEYS) {
    const value = theme[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      (flat as Record<string, number>)[key] = value;
    } else if (typeof value === 'string' && value.trim().length > 0) {
      (flat as Record<string, string>)[key] = value;
    }
  }

  return getDeviceFallbackConfig(flat, handoffTarget);
};
